import * as React from 'react'
import { Form, Input, Spin, Button, Tabs, Dropdown, Menu, Badge, message } from 'antd'
import {
  camelCase,
  cloneDeep,
  debounce,
  get,
  isEmpty,
  isEqual,
  isNil,
  set,
  toString as str,
  invoke,
  pick,
  flow,
} from 'lodash'
import produce from 'immer'
import { withRouter } from 'react-router-dom'
import { createPrintHandler, resetPrintHandler } from 'helpers/print'
import { setStorageItem, getStorageItem } from 'helpers/localStorage'
import { createResetState, DEBOUNCE, encodeState } from 'helpers/utils'
import {
  showError,
  showClientNotifications,
  resourceNotFound,
  ValidationError,
  ClientNotifications,
} from 'helpers/errors'
import {
  createLabelFactory,
  createFieldFactory,
  createHandleSubmit,
  createSaveDocumentItems,
  getChangedItems,
  isReadOnly,
  confirmUnsavedChanges,
  validateMultipleEmails,
} from 'helpers/formViews'
import { formatUserTime } from 'helpers/dateTime'
import { t } from 'helpers/i18n'
import { getUserPermission } from 'helpers/auth'
import { recalculateQuantity, BadgeContainer, getTagFieldNames } from 'helpers/procurement'
import { createSaveFormInstances, createRefreshFormInstances, FormInstancesContext } from 'helpers/formInstances'
import { Emitter, stopEvent } from 'helpers/events'
import { FORM_INSTANCES_REFRESH, LIST_MODAL_CLEAR_SELECTION, FORM_CHANGE_EVENT } from 'options/events'
import { getFileExtension } from 'helpers/downloadItems'
import UpdatePrices from 'containers/Rfqs/UpdatePrices'
import Select, { Option, getOptionProps } from 'elements/Select'
import Drawer from 'elements/Drawer'
import Modal from 'elements/Modal'
import Checkbox from 'elements/Checkbox'
import Comments from 'elements/Comments'
import Icon from 'elements/Icon'
import { Row, Col } from 'elements/Grid'
import Documents from 'containers/Documents/ChildListView'
import RfqItems from 'containers/Rfqs/Items'
import RfqSuppliers from 'containers/Rfqs/Suppliers'
import Help from 'elements/Help'
import SideForm from 'containers/Rfqs/FormView/SideForm'
import DatePicker from 'elements/DatePicker'
import SendEmail from 'components/SendEmail'
import FormButtons from 'elements/FormButtons'
import FormInstances from 'containers/Rfqs/Forms'
import Print from 'containers/Rfqs/Print'

const getStorageKey = () => 'rfqs.formView'

class FormView extends React.Component {
  state = {}

  constructor(props) {
    super(props)

    this.printableRef = React.createRef()
    this.handleSubmit = createHandleSubmit(this)
    this.saveDocumentItems = createSaveDocumentItems(this, 'Rfq')
    this.repopulateRfqItems = debounce(this.repopulateRfqItems, DEBOUNCE)
    this.resetState = createResetState(
      this,
      { tabsActiveKey: 'items', requireQuantity: true },
      getStorageItem(getStorageKey(), {})
    )
    this.saveFormInstances = createSaveFormInstances(this, { objectType: 'Rfq' })
    this.refreshFormInstances = createRefreshFormInstances(this, { objectType: 'Rfq' })
  }

  async componentDidMount() {
    await this.resetState()
    await this.fetchItem()

    Emitter.on(FORM_INSTANCES_REFRESH, this.refreshFormInstances)
    Emitter.on(FORM_CHANGE_EVENT, this.handleFormChange)
  }

  componentWillUnmount() {
    Emitter.off(FORM_INSTANCES_REFRESH, this.refreshFormInstances)
    Emitter.off(FORM_CHANGE_EVENT, this.handleFormChange)
  }

  handleFormChange = () => this.setState({ formHasChanged: true })

  beforeValidateFields = () => this.prioritizeRfqItemsSupplierDetails()

  promiseState = (state = {}) => new Promise((resolve) => this.setState(state, resolve))

  saveState = () => setStorageItem(getStorageKey(), pick(this.state, ['quickscan', 'requireQuantity']))

  getItemId = () => {
    const itemId = [
      this.state.item?.id,
      this.props.linkTargetRecord?.rfqId,
      this.props.linkTargetRecord?.relatedRfqId,
      this.props.linkTargetRecord?.objectId,
    ].find(Boolean)

    if (itemId && this.props.userIsInRoleSupplier) {
      const targetTenantId = this.props.filterDto?.tenantId ?? 0

      return `${itemId}?targetTenantId=${targetTenantId}`
    }

    return itemId
  }

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    try {
      const responses = await Promise.all([
        itemId
          ? this.props.getItem(itemId).catch((error) => {
              this.props.onCancel?.()
              throw error
            })
          : !this.props.linkTargetRecord
            ? this.props.newItem({})
            : resourceNotFound(this),
        this.props.getSettings({ type: this.props.settingsType ?? 'rfq' }),
      ])

      const item = cloneDeep(responses[0].value.data)
      const customerId = item.customerId || this.props.user.customerId
      const targetTenantId = this.props.filterDto?.tenantId ?? 0

      const [documentItems, shipToAddressList, rfqItems, rfqSupplierItems, formInstanceItems] = await Promise.all([
        item.id
          ? this.props.getDocumentItems({
              request: {
                domainObjectId: item.id,
                domainObjectType: 'Rfq',
                documentType: 'ObjectDocument',
              },
              targetTenantId,
            })
          : Promise.resolve(null),
        this.props.getAddressList({ type: 'shipto', targetTenantId, customerId }),
        item.id ? this.props.getRfqItems(item.id, { targetTenantId }) : Promise.resolve(null),
        item.id ? this.props.getRfqSupplierItems(item.id, { targetTenantId }) : Promise.resolve(null),
        item.id
          ? this.props.getFormInstanceItems({
              objectType: 'Rfq',
              objectId: item.id,
              targetTenantId,
            })
          : Promise.resolve(null),
      ])

      const populatedRfqItems = get(rfqItems, 'value.data.items', []).map((each) => ({
        ...each,
        populated: true,
        timestamp: new Date().toJSON(),
      }))

      this.setState({
        item: cloneDeep(item),
        itemOriginal: cloneDeep(item),
        fieldSettings: responses[1].value.data.fieldSettings,
        documentItems: get(documentItems, 'value.data.items', []),
        documentItemsOriginal: get(documentItems, 'value.data.items', []),
        shipToAddressList: get(shipToAddressList, 'value.data.items', []),
        rfqItems: cloneDeep(populatedRfqItems),
        rfqItemsOriginal: cloneDeep(populatedRfqItems),
        rfqItemsTotals: get(rfqItems, 'value.data.pageTotals', {}),
        rfqSupplierItems: get(rfqSupplierItems, 'value.data.items', []),
        rfqSupplierItemsOriginal: get(rfqSupplierItems, 'value.data.items', []),
        formInstanceItems: get(formInstanceItems, 'value.data.items', []),
        formInstanceItemsOriginal: get(formInstanceItems, 'value.data.items', []),
        formHasChanged: false,
      })
    } catch (error) {
      showError({ error })
    }
  }

  refetchItem = async ({ itemId = this.getItemId() } = {}) => {
    try {
      const response = await this.props.getItem(itemId).catch((error) => {
        this.props.onCancel?.()
        throw error
      })

      const item = cloneDeep(response.value.data)

      this.setState({
        item: cloneDeep(item),
        itemOriginal: cloneDeep(item),
      })
    } catch (error) {
      showError({ error })
    }
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        if (name === 'shipAddressId') {
          draft.item.taxRate = draft.shipToAddressList.find((one) => one.id === value).taxRate / 100
        }

        this.props.form.setFieldsValue({ [name]: value })
      })
    )

  saveItem = async (item) => {
    if (!item) {
      throw new Error('item is undefined')
    }

    try {
      this.setState({ clientNotifications: null })

      const targetTenantId = this.props.filterDto?.tenantId ?? 0

      const response = await this.props.saveRfq(
        {
          rfq: item,
          rfqItems: getChangedItems(this.state.rfqItemsOriginal, this.state.rfqItems),
          rfqSuppliers: getChangedItems(this.state.rfqSupplierItemsOriginal, this.state.rfqSupplierItems),
        },
        targetTenantId ? { targetTenantId } : undefined
      )

      const { failureCount = 0, clientNotifications = [], items = [] } = response.value.data

      showClientNotifications({ response })

      if (failureCount > 0) {
        this.setState({ clientNotifications })

        throw new Error()
      }

      const data = items[0]

      if (data.id) {
        await this.saveDocumentItems(data.id)
      }

      return { value: { data } }
    } catch (error) {
      const clientNotifications = error?.data?.clientNotifications ?? []

      showClientNotifications({ clientNotifications })

      if (!isEmpty(clientNotifications)) {
        this.setState({ clientNotifications })

        throw new Error()
      } else {
        throw error
      }
    }
  }

  handleSideFormSubmit = async (sideFormState) => {
    const {
      assetDisplayName = '',
      jobDisplayName = '',
      locationDisplayName = '',
      operatorDisplayName = '',
      ...params
    } = cloneDeep(sideFormState)

    try {
      this.setState({ sideFormLoading: true, tabsActiveKey: 'items', formButtonsDisabled: true })

      const response = await this.props.populateRfqItems([
        {
          quantity: params.quantity || 1,
          barcode: params?.barcode,
          assetBarcode: params.asset?.barcode ?? params.assetBarcode,
          assetId: params?.asset?.id,
          assetName: params.asset?.name ?? `*${assetDisplayName}`,
          assetNumber: params.asset?.number,
          jobId: params?.job?.id,
          jobName: params.job?.name ?? `*${jobDisplayName}`,
          jobNumber: params?.job?.number,
          jobBarcode:
            params.job?.barcode ?? (!this.props.customer.tagSettings.tag1Restricted ? jobDisplayName : undefined),
          locationId: params?.location?.id,
          locationName: params.location?.name ?? `*${locationDisplayName}`,
          locationBarcode: params.location?.barcode ?? params.locationBarcode,
          operatorId: params?.operator?.id,
          operatorName: params.operator?.name ?? `*${operatorDisplayName}`,
          operatorNumber: params?.operator?.number,
          operatorBarcode: params.operator?.barcode ?? params.operatorBarcode,
          tagA: params.tagA,
          tagB: params.tagB,
          tagC: params.tagC,
          tagD: params.tagD,
          tagE: params.tagE,
          userName: this.props.user.userName,
          sourceCatalogName: params.sourceCatalogName,
        },
      ])

      this.setState({ sideFormLoading: false })

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }

      this.setState(
        produce((draft) => {
          const seed = Date.now() * -1
          draft.rfqItems.unshift(
            ...response.value.data.items.map((each, index) => ({
              ...each,
              quantity: recalculateQuantity(each, this.props.customer),
              id: seed - index,
              rfqItemId: seed - index,
            }))
          )
        }),
        this.prioritizeRfqItemsSupplierDetails
      )
    } catch (error) {
      showError({ error })

      this.setState({ sideFormLoading: false })

      return false
    } finally {
      this.setState({ formButtonsDisabled: false })
    }

    return true
  }

  repopulateRfqItems = async () => {
    let failureCount = 0

    try {
      const { rfqItems } = this.state

      const populatedRfqItems = await Promise.all(
        rfqItems.map((each) => {
          const { id, rfqItemId, populated } = each

          if (populated) {
            return each
          }

          return this.props
            .populateRfqItems([
              {
                ...each,
                id: id > 0 ? id : undefined,
                rfqItemId: rfqItemId > 0 ? rfqItemId : undefined,
              },
            ])
            .then((response) => {
              showClientNotifications({ response })

              failureCount += response.value.data.failureCount

              return {
                ...(response.value.data.items[0] ?? each),
                id,
                rfqItemId,
                populated: true,
                timestamp: new Date().toJSON(),
              }
            })
        })
      )

      await this.promiseState({ rfqItems: cloneDeep(populatedRfqItems) })
      await this.prioritizeRfqItemsSupplierDetails()
    } catch (error) {
      failureCount++
      showError({ error })
    }

    return failureCount
  }

  handleRfqItemsChange = async (changed = []) => {
    let failureCount = 0
    let values = changed

    try {
      if (changed.some((one) => one.copyTagValuesToAllItems)) {
        const item = { ...changed.find((one) => one.copyTagValuesToAllItems), copyTagValuesToAllItems: false }
        const tagValues = pick(item, getTagFieldNames())
        const otherItems = changed.filter((each) => each.id !== item.id).map((each) => ({ ...each, ...tagValues }))
        values = [item, ...otherItems]
      }

      await this.promiseState({
        formButtonsDisabled: true,
        rfqItemsLoading: true,
        rfqItems: values,
        tabsActiveKey: 'items',
      })

      if (!this.props.userIsInRoleSupplier) {
        failureCount += await this.repopulateRfqItems()
      }
    } catch (error) {
      failureCount++
      showError({ error })
    } finally {
      this.setState({ formButtonsDisabled: false, rfqItemsLoading: false })
    }

    if (failureCount > 0) {
      throw new Error()
    }
  }

  invokeAction = async (action) => {
    try {
      await this.props.onSave?.(true)

      this.setState({ clientNotifications: null })

      const response = await invoke(this.props, action, this.state.item.id)

      const { failureCount = 0, clientNotifications = [] } = response.value.data

      showClientNotifications({ response })

      if (failureCount > 0) {
        this.setState({ clientNotifications })

        throw new Error()
      }

      await this.refetchItem()
    } catch (error) {
      const clientNotifications = error?.data?.clientNotifications ?? []

      showClientNotifications({ clientNotifications })

      if (!isEmpty(clientNotifications)) {
        this.setState({ clientNotifications })
      } else {
        showError({ error })
      }
    } finally {
      await this.props.onSave?.(false)
    }
  }

  handleActionsMenuClick = async ({ key }) => {
    switch (key) {
      case 'text/csv':
      case 'text/html':
      case 'application/pdf':
      case 'application/vnd.ms-excel':
        if (this.hasUnsavedChanges()) {
          message.error(t('saveChangesFirst'))
        } else {
          Emitter.emit('rfqs.rfqItems.downloadItems', {
            parentId: this.state.item.id,
            acceptMimeType: key,
            pageTitle: `${t('rfq')} - ${this.state.item.id} - ${t('itemsTab')}`,
            fileExtension: getFileExtension(key),
          })
        }
        break

      case 'updatePrices':
        if (this.hasUnsavedChanges()) {
          message.error(t('saveChangesFirst'))
        } else {
          try {
            const { id } = this.state.item
            const response = await this.props.getSupplierPrices(id)

            this.setState({
              updatePricesItems: response.value.data.map((each, index) => ({
                ...each,
                id: index + 1,
              })),
              updatePricesVisible: true,
              updatePricesRecords: null,
            })
          } catch (error) {
            showError({ error })
          }
        }
        break

      case 'closeRfq':
      case 'cancelRfq':
        if (this.hasUnsavedChanges()) {
          message.error(t('saveChangesFirst'))
        } else {
          Modal.confirm({
            autoFocusButton: 'ok',
            title: t(camelCase(`confirm-${key}`)),
            content: t(camelCase(`confirm-${key}-description`)),
            okText: t('yes'),
            okType: 'danger',
            cancelText: t('no'),
            onOk: () => this.invokeAction(key),
          })
        }
        break

      case 'deleteRfq':
        Modal.confirm({
          autoFocusButton: 'ok',
          title: t('confirmDeleteRfq'),
          content: t('confirmDeleteRfqDescription'),
          okText: t('delete'),
          okType: 'danger',
          cancelText: t('cancel'),
          onOk: async () => {
            try {
              const response = await this.props.deleteRfq(this.state.item.id)

              showClientNotifications({ response })

              if (response.value.data.failureCount > 0) {
                throw new Error()
              }

              this.props.onSaveAndClose?.()
            } catch (error) {
              showError({ error })
            }
          },
        })
        break

      case 'sendEmail':
        if (this.hasUnsavedChanges()) {
          message.error(t('saveChangesFirst'))
        } else {
          this.setState({ sendEmailVisible: true })
        }
        break

      case 'rfqAuditReport':
        confirmUnsavedChanges(() => {
          this.props.history.push(
            `/reports/audit/rfqAuditReport?${encodeState({
              search: str(this.state.item.id),
              searchFields: ['objectId'],
              searchType: 'Contains',
              showFilter: true,
              filterDto: { dateRange: { dateRangeType: 'LastNMonths', lastNValue: 12 } },
            })}`
          )
          this.props.onCancel()
        })
        break

      case 'print':
        this.setState(
          produce((draft) => {
            draft.printItem = { ...draft.item, ...this.props.form.getFieldsValue() }
            draft.printVisible = true
          })
        )
        break

      default:
        message.warn(t('underDevelopment'))
        break
    }
  }

  invokeExtraAction = async (action) => {
    const { item } = this.state

    if (this.hasUnsavedChanges()) {
      message.error(t('saveChangesFirst'))
      return
    }

    try {
      await this.props.onSave?.(true)

      this.setState({
        [`${action}ButtonLoading`]: true,
        clientNotifications: null,
      })

      if (this.props[action]) {
        const targetTenantId = this.props.filterDto?.tenantId ?? 0

        const response = await invoke(this.props, action, item.id, { targetTenantId })

        showClientNotifications({ response })

        if (response.value.data.failureCount > 0) {
          throw new Error()
        }
      }

      await this.refetchItem()
      await this.props.onSave?.(false)
    } catch (error) {
      showError({ error })
      await this.props.onSave?.(false)
    } finally {
      this.setState({ [`${action}ButtonLoading`]: false })
    }
  }

  hasUnsavedChanges = () =>
    this.state.formHasChanged ||
    !this.state.item?.id ||
    !isEqual(this.state.item, this.state.itemOriginal) ||
    !isEqual(this.state.rfqItems, this.state.rfqItemsOriginal) ||
    !isEqual(this.state.rfqSupplierItems, this.state.rfqSupplierItemsOriginal) ||
    !isEqual(this.state.documentItems, this.state.documentItemsOriginal)

  handleAddFromList = async (sideFormState) => {
    const {
      assetDisplayName = '',
      jobDisplayName = '',
      locationDisplayName = '',
      operatorDisplayName = '',
      ...params
    } = cloneDeep(sideFormState)

    const asset = (params?.assets?.items ?? []).find((one) => one.id === params.assetId)
    const job = (params?.jobs?.items ?? []).find((one) => one.id === params.jobId)
    const location = (params?.locations?.items ?? []).find((one) => one.id === params.locationId)
    const operator = (params?.operators?.items ?? []).find((one) => one.id === params.operatorId)

    this.setState({ addFromListLoading: true, tabsActiveKey: 'items' })

    try {
      const response = await this.props.populateRfqItems(
        params.addFromListItems.map((each) => ({
          quantity: Math.max(each.quantity, 1),
          barcode: each.barcode,
          assetId: asset?.id,
          assetName: asset?.name ?? `*${assetDisplayName}`,
          assetNumber: asset?.number,
          assetBarcode: asset?.barcode,
          jobId: job?.id,
          jobName: job?.name ?? `*${jobDisplayName}`,
          jobNumber: job?.number,
          jobBarcode: job?.barcode ?? jobDisplayName,
          locationId: location?.id,
          locationName: location?.name ?? `*${locationDisplayName}`,
          locationNumber: location?.number,
          locationBarcode: location?.barcode,
          operatorId: operator?.id,
          operatorName: operator?.name ?? `*${operatorDisplayName}`,
          operatorNumber: operator?.number,
          operatorBarcode: operator?.barcode,
          tagA: params.tagA,
          tagB: params.tagB,
          tagC: params.tagC,
          tagD: params.tagD,
          tagE: params.tagE,
          userName: this.props.user.userName,
        }))
      )

      this.setState({ sideFormLoading: false })

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }

      response.value.data.items.map((each) => message.success(`${each.barcode} ${t('addToRfqSuccess')}`))

      this.setState(
        produce((draft) => {
          const seed = Date.now() * -1
          draft.rfqItems.unshift(
            ...response.value.data.items.map((each, index) => ({
              ...each,
              quantity: recalculateQuantity(each, this.props.customer),
              id: seed - index,
              rfqItemId: seed - index,
            }))
          )
        }),
        this.prioritizeRfqItemsSupplierDetails
      )

      Emitter.emit(LIST_MODAL_CLEAR_SELECTION)
    } catch (error) {
      showError({ error })

      return false
    } finally {
      this.setState({ addFromListLoading: false })
    }

    return true
  }

  handleSendEmailSubmit = async (recipients) => {
    try {
      this.setState({ sendEmailLoading: true })

      const response = await this.props.emailRfq({
        recipients,
        rfq: this.state.item ?? {},
        rfqItems: this.state.rfqItems ?? [],
      })

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }

      this.setState({
        sendEmailLoading: false,
        sendEmailVisible: false,
      })
    } catch (error) {
      showError({ error })
      this.setState({ sendEmailLoading: false })
    }
  }

  handleRfqSuppliersItemsChange = (values) =>
    this.setState({ rfqSupplierItems: values }, this.prioritizeRfqItemsSupplierDetails)

  prioritizeRfqItemsSupplierDetails = () =>
    this.setState(
      produce((draft) => {
        draft.rfqSupplierItems.forEach((each, index) => {
          each.supplierPosition = index
        })

        draft.rfqItems.forEach((rfqItem) => {
          rfqItem.supplierDetails = draft.rfqSupplierItems.map((each) => {
            const existing = rfqItem.supplierDetails.find((one) => one.supplierId === each.supplierId)
            return {
              supplierName: each.supplierName,
              price: existing?.price ?? 0,
              comment: get(existing, 'comment', ''),
              supplierId: each.supplierId,
              supplierPosition: each.supplierPosition,
            }
          })
        })
      })
    )

  render() {
    const { settingsType, userIsInRoleSupplier, userIsDcribDistributor } = this.props
    const { item, fieldSettings, documentItems, rfqItems, rfqSupplierItems, quickscan, requireQuantity } = this.state

    if (!item || !fieldSettings) {
      return <Spin />
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)

    const readOnly = isReadOnly(this) || ['Cancelled', 'Closed'].includes(item.status)
    const targetTenantId = this.props.filterDto?.tenantId ?? 0
    const extraButtonsDisabled =
      this.state.formButtonsDisabled || this.state.saveButtonLoading || this.state.saveAndCloseButtonLoading

    return (
      <>
        <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
          {item.id ? (
            <Dropdown
              overlay={
                userIsInRoleSupplier ? (
                  <Menu onClick={this.handleActionsMenuClick}>
                    <Menu.SubMenu key="export" title={t('export')}>
                      <Menu.Item key="text/csv">{t('asCsv')}</Menu.Item>
                      <Menu.Item key="application/vnd.ms-excel">{t('asExcel')}</Menu.Item>
                      <Menu.Item key="application/pdf">{t('asPdf')}</Menu.Item>
                      <Menu.Item key="text/html">{t('asHtml')}</Menu.Item>
                    </Menu.SubMenu>
                  </Menu>
                ) : (
                  <Menu onClick={this.handleActionsMenuClick}>
                    <Menu.Item key="updatePrices" disabled={readOnly || item.status === 'Draft'}>
                      {t('updatePrices')}
                    </Menu.Item>
                    <Menu.Divider />
                    {getUserPermission('Reports') !== 'No' &&
                      getUserPermission('Rfqs') !== 'None' && [
                        <Menu.Item key="rfqAuditReport">{t('rfqAuditReport')}</Menu.Item>,
                        <Menu.Divider key="divider" />,
                      ]}
                    <Menu.SubMenu key="export" title={t('export')}>
                      <Menu.Item key="text/csv">{t('asCsv')}</Menu.Item>
                      <Menu.Item key="application/vnd.ms-excel">{t('asExcel')}</Menu.Item>
                      <Menu.Item key="application/pdf">{t('asPdf')}</Menu.Item>
                      <Menu.Item key="text/html">{t('asHtml')}</Menu.Item>
                    </Menu.SubMenu>
                    <Menu.Item key="sendEmail">{t('email')}</Menu.Item>
                    {['Draft', 'Open', 'Closed'].includes(item.status) && <Menu.Divider />}
                    {item.status === 'Open' && (
                      <Menu.Item key="closeRfq" disabled={readOnly}>
                        {t('closeRfq')}
                      </Menu.Item>
                    )}
                    {['Open', 'Closed'].includes(item.status) && (
                      <Menu.Item key="cancelRfq" disabled={readOnly && item.status !== 'Closed'}>
                        {t('cancelRfq')}
                      </Menu.Item>
                    )}
                    {item.status === 'Draft' && (
                      <Menu.Item key="deleteRfq" disabled={readOnly}>
                        {t('deleteRfq')}
                      </Menu.Item>
                    )}
                    <Menu.Divider />
                    <Menu.Item key="print" disabled={!item.id}>
                      {t('print...')}
                    </Menu.Item>
                  </Menu>
                )
              }
              trigger={['click']}
            >
              <Button className="form-items-actions">
                {t('actions')}
                <Icon type="KeyboardArrowDown" />
              </Button>
            </Dropdown>
          ) : null}
          <div className="form-items-container">
            <ValidationError errors={this.state.validationErrors} />
            <ClientNotifications notifications={this.state.clientNotifications} />
            <Row>
              <Col xs={24} lg={6}>
                <Form.Item label={t('rfqId')}>
                  <Input value={item.id} disabled />
                </Form.Item>
              </Col>
              <Col xs={24} lg={6}>
                <Form.Item label={createLabel('createDate')}>
                  <Input value={formatUserTime(item.createDate, item.userName) || t('na')} disabled />
                </Form.Item>
              </Col>
              <Col xs={24} lg={6}>
                <Form.Item label={createLabel('statusName')}>
                  <Input value={item.statusName} disabled />
                </Form.Item>
              </Col>
            </Row>
            <Tabs
              activeKey={this.state.tabsActiveKey}
              onChange={(tabsActiveKey) => this.setState({ tabsActiveKey })}
              defaultActiveKey="items"
            >
              <Tabs.TabPane key="rfq" tab={t('rfq')} forceRender>
                <Row>
                  <Col xs={24} lg={12}>
                    <Form.Item label={createLabel('shipToAddressId')}>
                      {createFieldDecorator('shipToAddressId')(
                        <Select
                          onChange={(value) => this.setItemValue('shipToAddressId', value)}
                          placeholder={t('select')}
                          allowClear={false}
                          readOnly={readOnly || userIsInRoleSupplier}
                        >
                          {(this.state.shipToAddressList ?? []).map((each) => (
                            <Option key={each.id} value={each.id}>
                              <span {...getOptionProps(each)}>{each.displayName}</span>
                            </Option>
                          ))}
                        </Select>
                      )}
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={6}>
                    <Form.Item label={createLabel('rfqNumber')}>
                      <Input
                        value={item.rfqNumber}
                        onChange={(e) => this.setItemValue('rfqNumber', e.target.value)}
                        readOnly={readOnly || userIsInRoleSupplier}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col xs={24} lg={6}>
                    <Form.Item label={createLabel('requisitionerName')}>
                      {createFieldDecorator('requisitionerName')(
                        <Input
                          onChange={(e) => this.setItemValue('requisitionerName', e.target.value)}
                          readOnly={readOnly || userIsInRoleSupplier}
                        />
                      )}
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={6}>
                    <Form.Item label={createLabel('buyerName')}>
                      {createFieldDecorator('buyerName')(
                        <Input
                          onChange={(e) => this.setItemValue('buyerName', e.target.value)}
                          readOnly={readOnly || userIsInRoleSupplier}
                        />
                      )}
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={6}>
                    <Form.Item label={createLabel('responseDueDate')}>
                      {createFieldDecorator('responseDueDate')(
                        <DatePicker
                          onChange={(value) => this.setItemValue('responseDueDate', value)}
                          readOnly={readOnly || userIsInRoleSupplier}
                        />
                      )}
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col xs={24} lg={6}>
                    <Form.Item label={createLabel('requisitionerPhone')}>
                      {createFieldDecorator('requisitionerPhone')(
                        <Input
                          onChange={(e) => this.setItemValue('requisitionerPhone', e.target.value)}
                          type="tel"
                          readOnly={readOnly || userIsInRoleSupplier}
                        />
                      )}
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={6}>
                    <Form.Item label={createLabel('buyerPhone')}>
                      {createFieldDecorator('buyerPhone')(
                        <Input
                          onChange={(e) => this.setItemValue('buyerPhone', e.target.value)}
                          type="tel"
                          readOnly={readOnly || userIsInRoleSupplier}
                        />
                      )}
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={6}>
                    <Form.Item label={createLabel('deliveryDueDate')}>
                      {createFieldDecorator('deliveryDueDate')(
                        <DatePicker
                          onChange={(value) => this.setItemValue('deliveryDueDate', value)}
                          readOnly={readOnly || userIsInRoleSupplier}
                        />
                      )}
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col xs={24} lg={6}>
                    <Form.Item
                      label={
                        <span>
                          {createLabel('requisitionerEmail')} <Help title={t('multipleEmailsHelp')} />
                        </span>
                      }
                    >
                      {createFieldDecorator('requisitionerEmail', { extraRules: [validateMultipleEmails()] })(
                        <Input
                          onChange={(e) => this.setItemValue('requisitionerEmail', e.target.value)}
                          readOnly={readOnly || userIsInRoleSupplier}
                        />
                      )}
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={6}>
                    <Form.Item label={createLabel('buyerEmail')}>
                      {createFieldDecorator('buyerEmail')(
                        <Input
                          onChange={(e) => this.setItemValue('buyerEmail', e.target.value)}
                          type="email"
                          readOnly={readOnly || userIsInRoleSupplier}
                        />
                      )}
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col xs={24} lg={18}>
                    <Comments
                      value={item.customerComment}
                      onChange={(value) => this.setItemValue('customerComment', value)}
                      rows={7}
                      label={createLabel('customerComment')}
                      title={t('addComment')}
                      readOnly={readOnly || userIsInRoleSupplier}
                    />
                  </Col>
                </Row>
              </Tabs.TabPane>
              <Tabs.TabPane key="items" tab={t('itemsTab')} forceRender>
                <RfqItems
                  items={this.state.rfqItems ?? []}
                  pageTotals={this.state.rfqItemsTotals ?? {}}
                  onChange={this.handleRfqItemsChange}
                  settingsType={settingsType}
                  parentRecord={{
                    ...item,
                    rfqItems,
                    rfqSupplierItems,
                    documentItems,
                    fieldSettings,
                  }}
                  parentHasUnsavedChanges={this.hasUnsavedChanges()}
                  readOnly={readOnly}
                  onCreateOrder={() => {}}
                  onCreatedOrderDrawerClose={this.fetchItem}
                  downloadItems={this.props.downloadRfqItems}
                  loading={this.state.rfqItemsLoading}
                />
              </Tabs.TabPane>
              {!userIsInRoleSupplier && (
                <Tabs.TabPane key="suppliers" tab={t('suppliers')} forceRender>
                  <RfqSuppliers
                    items={this.state.rfqSupplierItems ?? []}
                    pageTotals={this.state.rfqSupplierItems ?? {}}
                    onChange={this.handleRfqSuppliersItemsChange}
                    settingsType={settingsType}
                    parentRecord={{
                      ...item,
                      rfqItems,
                      rfqSupplierItems,
                      documentItems,
                      fieldSettings,
                    }}
                    siblingRecords={rfqSupplierItems}
                    readOnly={readOnly}
                    parentHasUnsavedChanges={this.hasUnsavedChanges()}
                    targetTenantId={targetTenantId}
                  />
                </Tabs.TabPane>
              )}
              <Tabs.TabPane key="documents" tab={t('documents')} forceRender>
                <Documents
                  domainObjectId={item.id}
                  domainObjectType="Rfq"
                  items={this.state.documentItems ?? []}
                  onChange={(values) => this.setState({ documentItems: values })}
                  readOnly={readOnly || userIsInRoleSupplier}
                />
              </Tabs.TabPane>
              {!isNil(item.id) &&
                !userIsInRoleSupplier &&
                (getUserPermission('Master') === 'Yes' || getUserPermission('Forms') !== 'None') && (
                  <Tabs.TabPane key="forms" tab={t('forms')} forceRender>
                    <FormInstancesContext.Provider value={{ targetTenantId }}>
                      <FormInstances
                        settingsType="rfq"
                        objectType="Rfq"
                        objectId={item.id}
                        parentRecord={item}
                        items={this.state.formInstanceItems ?? []}
                        onChange={this.saveFormInstances}
                        readOnly={readOnly}
                      />
                    </FormInstancesContext.Provider>
                  </Tabs.TabPane>
                )}
            </Tabs>
          </div>
          <div className="form-buttons-container">
            <div className="float-right text-right">
              <FormButtons
                readOnly={readOnly}
                onCancel={this.props.onCancel}
                onSubmit={this.handleSubmit}
                saveButtonLoading={this.state.saveButtonLoading}
                saveAndCloseButtonLoading={this.state.saveAndCloseButtonLoading}
                disabled={this.state.formButtonsDisabled}
              />
            </div>
            {!userIsInRoleSupplier && !userIsDcribDistributor && (
              <div className="float-left text-left">
                {item.status === 'Draft' && item.userAuthorizedForAllLocations && (
                  <Button
                    type="primary"
                    onClick={() => this.invokeExtraAction('sendRfq')}
                    loading={this.state.sendRfqButtonLoading}
                    disabled={isEmpty(rfqItems) || readOnly || extraButtonsDisabled}
                  >
                    {t('send')}
                  </Button>
                )}{' '}
                {item.status === 'Open' && item.userAuthorizedForAllLocations && (
                  <Button
                    type="primary"
                    onClick={() => this.invokeExtraAction('resendRfq')}
                    loading={this.state.resendRfqButtonLoading}
                    disabled={isEmpty(rfqItems) || readOnly || extraButtonsDisabled}
                  >
                    {t('resend')}
                  </Button>
                )}{' '}
                {item.status === 'Closed' && item.userAuthorizedForAllLocations && (
                  <Button
                    type="primary"
                    onClick={() =>
                      this.hasUnsavedChanges()
                        ? message.error(t('saveChangesFirst'))
                        : this.invokeExtraAction('reopenRfq')
                    }
                    loading={this.state.reopenRfqButtonLoading}
                    disabled={isEmpty(rfqItems) || extraButtonsDisabled}
                  >
                    {t('reopen')}
                  </Button>
                )}
              </div>
            )}
          </div>
        </Form>
        {!readOnly && getUserPermission('Purchase') !== 'No' && !userIsInRoleSupplier && (
          <Drawer title={t('addToRfq')} visible sideDrawer>
            <div className="form-items-actions text-right inline-block">
              <BadgeContainer>
                <Badge {...(quickscan ? { count: '!' } : {})}>
                  <Button onClick={() => this.setState({ showDataEntrySettings: true })}>
                    <Icon type="Settings" />
                  </Button>
                </Badge>
              </BadgeContainer>
            </div>
            <SideForm
              quickscan={quickscan}
              requireQuantity={requireQuantity}
              onSubmitting={(pending) => this.setState({ formButtonsDisabled: pending, rfqItemsLoading: pending })}
              onSubmit={this.handleSideFormSubmit}
              loading={this.state.sideFormLoading}
              parentRecord={{
                ...item,
                rfqItems,
                documentItems,
                fieldSettings,
              }}
              parentHasUnsavedChanges={this.hasUnsavedChanges()}
              onPunchout={() => this.setState({ tabsActiveKey: 'items' }, this.fetchItem)}
              onAddFromList={this.handleAddFromList}
              addFromListLoading={this.state.addFromListLoading}
              domainObjectType="Rfq"
              disabled={extraButtonsDisabled}
            />
          </Drawer>
        )}
        <Modal
          title={t('dataEntrySettings')}
          visible={this.state.showDataEntrySettings}
          onCancel={() => this.setState({ showDataEntrySettings: false })}
          footer={null}
        >
          <Form.Item>
            <Checkbox
              checked={quickscan}
              onChange={(e) => this.setState({ quickscan: e.target.checked }, this.saveState)}
            >
              {t('quickscan')} <Help title={t('quickScanInfo')} />
            </Checkbox>
          </Form.Item>
          <Form.Item>
            <Checkbox
              checked={requireQuantity}
              onChange={(e) => this.setState({ requireQuantity: e.target.checked }, this.saveState)}
            >
              {t('requireQuantity')}
            </Checkbox>
          </Form.Item>
        </Modal>
        <SendEmail
          visible={this.state.sendEmailVisible}
          loading={this.state.sendEmailLoading}
          onSubmit={this.handleSendEmailSubmit}
          onCancel={() => this.setState({ sendEmailVisible: false })}
        />
        <Modal
          title={t('updatePrices')}
          visible={this.state.updatePricesVisible}
          okText={t('updatePrices')}
          okButtonProps={{
            disabled: isEmpty(this.state.updatePricesRecords),
            loading: this.state.updatePricesLoading,
          }}
          onOk={async () => {
            try {
              this.setState({ updatePricesLoading: true })

              const response = await this.props.updatePrices(
                this.state.updatePricesRecords.map((each) => ({
                  supplierId: each.supplierId,
                  barcode: each.barcode,
                  price: each.newPrice,
                }))
              )

              showClientNotifications({ response })

              if (response.value.data.failureCount > 0) {
                throw new Error()
              }

              this.setState({ updatePricesVisible: false })
            } catch (error) {
              showError({ error })
            } finally {
              this.setState({ updatePricesLoading: false })
            }
          }}
          onCancel={() => this.setState({ updatePricesVisible: false })}
          width={992}
        >
          <UpdatePrices
            items={this.state.updatePricesItems}
            onSelect={(values) => this.setState({ updatePricesRecords: values })}
            mode="multiple"
          />
        </Modal>
        <Modal
          title={t('printPreview')}
          okText={t('print')}
          onOk={createPrintHandler(this.printableRef)}
          onCancel={() => this.setState({ printVisible: false }, resetPrintHandler)}
          width={992}
          visible={this.state.printVisible}
        >
          <div ref={this.printableRef} className="tofino-print-preview">
            <Print
              parentRecord={{
                ...this.state.printItem,
                rfqItems,
                rfqSupplierItems,
              }}
            />
          </div>
        </Modal>
      </>
    )
  }
}

export default flow(
  withRouter,
  Form.create({
    onValuesChange: (...params) => Emitter.emit(FORM_CHANGE_EVENT, ...params),
  })
)(FormView)
